import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatDate } from '@/lib/utils'
import { CheckSquare, Square, Phone, Mail, ClipboardList, Clock } from 'lucide-react'
import { toast } from 'sonner'

/**
 * My Tasks — a standalone triage view of the current user's assigned sales_tasks
 * (Sales Slice 2 CP2b). Read + complete only; creating/editing tasks stays on
 * TasksThread (the per-record Tasks tab/sidebar). Tasks are bucketed by due date
 * into Overdue / Today / Upcoming / Done chips; each row links to the parent
 * record (deal/contact/company). sales_tasks isn't in the generated types — cast.
 */

type Bucket = 'overdue' | 'today' | 'upcoming' | 'done'

interface TaskRow {
  id: string
  entity_type: string
  entity_id: string
  title: string
  task_type: string
  status: string
  due_date: string | null
  remind_at: string | null
}

const TYPE_META: Record<string, { label: string; Icon: React.ComponentType<{ style?: React.CSSProperties }> }> = {
  todo: { label: 'To-do', Icon: ClipboardList },
  call: { label: 'Call', Icon: Phone },
  email: { label: 'Email', Icon: Mail },
}

export default function Tasks() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { profile, workspace } = useAuth()
  const meId = profile?.id ?? null
  const wsId = workspace?.id ?? null

  // My assigned tasks (served by sales_tasks_assignee_idx).
  const { data: tasks = [], isLoading } = useQuery<TaskRow[]>({
    queryKey: ['my-tasks', wsId, meId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('sales_tasks')
        .select('*')
        .eq('workspace_id', wsId)
        .eq('assigned_to', meId)
        .order('due_date', { ascending: true, nullsFirst: false })
      if (error) throw error
      return (data ?? []) as TaskRow[]
    },
    enabled: !!wsId && !!meId,
  })

  // Group entity ids by type for batched parent-name resolution.
  const { dealIds, contactIds, companyIds } = useMemo(() => {
    const d = new Set<string>(), c = new Set<string>(), co = new Set<string>()
    for (const t of tasks) {
      if (t.entity_type === 'deal') d.add(t.entity_id)
      else if (t.entity_type === 'contact') c.add(t.entity_id)
      else if (t.entity_type === 'company') co.add(t.entity_id)
    }
    return { dealIds: [...d], contactIds: [...c], companyIds: [...co] }
  }, [tasks])

  // 3 batched IN queries (skip empty groups → never fire an IN with []).
  const { data: parentMap = new Map<string, { name: string; link: string }>() } = useQuery<Map<string, { name: string; link: string }>>({
    queryKey: ['my-tasks-parents', dealIds.slice().sort().join(','), contactIds.slice().sort().join(','), companyIds.slice().sort().join(',')],
    queryFn: async () => {
      const m = new Map<string, { name: string; link: string }>()
      if (dealIds.length) {
        const { data } = await supabase.from('deals').select('id,title').in('id', dealIds)
        for (const r of (data ?? []) as any[]) m.set(`deal:${r.id}`, { name: r.title || 'Deal', link: `/app/deals/${r.id}` })
      }
      if (contactIds.length) {
        const { data } = await supabase.from('contacts').select('id,first_name,last_name,company_name').in('id', contactIds)
        for (const r of (data ?? []) as any[]) m.set(`contact:${r.id}`, { name: `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || 'Contact', link: `/app/contacts/${r.id}` })
      }
      if (companyIds.length) {
        const { data } = await supabase.from('companies').select('id,name').in('id', companyIds)
        for (const r of (data ?? []) as any[]) m.set(`company:${r.id}`, { name: r.name || 'Company', link: `/app/companies/${r.id}` })
      }
      return m
    },
    enabled: tasks.length > 0,
  })

  // Client-side due bucketing (per-user task volumes are small; index-served fetch).
  const buckets = useMemo(() => {
    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
    const overdue: TaskRow[] = [], today: TaskRow[] = [], upcoming: TaskRow[] = [], done: TaskRow[] = []
    for (const t of tasks) {
      if (t.status === 'done') { done.push(t); continue }
      const d = t.due_date ? new Date(t.due_date) : null
      if (d && d < todayStart) overdue.push(t)
      else if (d && d >= todayStart && d <= todayEnd) today.push(t)
      else upcoming.push(t) // no due date, or due after today
    }
    return { overdue, today, upcoming, done }
  }, [tasks])

  const [chip, setChip] = useState<Bucket>('today')
  const visible = buckets[chip]

  const toggleComplete = useMutation({
    mutationFn: async (t: TaskRow) => {
      const done = t.status !== 'done'
      const { error } = await (supabase as any).from('sales_tasks')
        .update({ status: done ? 'done' : 'open', completed_at: done ? new Date().toISOString() : null })
        .eq('id', t.id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-tasks', wsId, meId] }),
    onError: (e: any) => toast.error(e?.message || 'Failed to update task'),
  })

  const CHIPS: { key: Bucket; label: string; count: number }[] = [
    { key: 'overdue', label: 'Overdue', count: buckets.overdue.length },
    { key: 'today', label: 'Today', count: buckets.today.length },
    { key: 'upcoming', label: 'Upcoming', count: buckets.upcoming.length },
    { key: 'done', label: 'Done', count: buckets.done.length },
  ]
  const emptyMsg: Record<Bucket, string> = {
    overdue: 'Nothing overdue', today: 'Nothing due today', upcoming: 'Nothing upcoming', done: 'Nothing completed',
  }
  const now2 = new Date()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '24px 28px 20px', flexShrink: 0 }}>
        <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>My Tasks</h1>
        <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
          {buckets.overdue.length} overdue · {buckets.today.length} due today
        </p>
        <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
          {CHIPS.map(c => (
            <button key={c.key} onClick={() => setChip(c.key)}
              style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, background: chip === c.key ? 'var(--accent)' : 'var(--hover-bg)', color: chip === c.key ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: chip === c.key ? 'none' : '1px solid var(--border-subtle)' }}>
              {c.label}
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 100, background: chip === c.key ? 'var(--accent-dim)' : 'var(--border-subtle)', color: chip === c.key ? 'var(--accent-text)' : 'var(--ink-tertiary)' }}>{c.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--ink-tertiary)', fontSize: 13 }}>{emptyMsg[chip]}</div>
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden' }}>
            {visible.map(t => {
              const meta = TYPE_META[t.task_type] || TYPE_META.todo
              const Icon = meta.Icon
              const parent = parentMap.get(`${t.entity_type}:${t.entity_id}`)
              const done = t.status === 'done'
              const overdue = !done && t.due_date && new Date(t.due_date) < now2
              return (
                <div key={t.id} onClick={() => { if (parent) navigate(parent.link) }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', cursor: parent ? 'pointer' : 'default', transition: 'background 150ms ease' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
                  <button type="button" onClick={e => { e.stopPropagation(); toggleComplete.mutate(t) }} aria-label={done ? 'Reopen task' : 'Complete task'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: done ? 'var(--positive)' : 'var(--ink-tertiary)', display: 'flex', flexShrink: 0 }}>
                    {done ? <CheckSquare style={{ width: 16, height: 16 }} /> : <Square style={{ width: 16, height: 16 }} />}
                  </button>
                  <Icon style={{ width: 13, height: 13, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: done ? 'var(--ink-tertiary)' : 'var(--ink-primary)', textDecoration: done ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.title}
                    {parent && <span style={{ fontWeight: 400, color: 'var(--ink-tertiary)' }}> — {parent.name}</span>}
                  </span>
                  {t.remind_at && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--ink-tertiary)', flexShrink: 0 }}>
                      <Clock style={{ width: 10, height: 10 }} /> {formatDate(t.remind_at, 'MMM d, HH:mm')}
                    </span>
                  )}
                  {t.due_date && (
                    <span style={{ fontSize: 12, fontWeight: overdue ? 700 : 400, color: overdue ? 'var(--danger)' : 'var(--ink-secondary)', flexShrink: 0 }}>
                      {formatDate(t.due_date, 'MMM d')}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
